<template>
  <q-page class="admin-page">
    <div class="admin-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div>
          <q-skeleton type="text" width="220px" dark style="font-size: 1.75rem;" />
          <q-skeleton type="text" width="300px" dark style="margin-top: 4px;" />
        </div>
        <div class="stats-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 8" :key="n" type="rect" height="90px" dark style="border-radius: 12px;" />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 24px;">
          <q-skeleton type="rect" height="240px" dark style="border-radius: 12px;" />
          <q-skeleton type="rect" height="240px" dark style="border-radius: 12px;" />
        </div>
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="admin-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="admin-header">
          <h1 class="admin-title">Panel de Administracion</h1>
          <p class="admin-subtitle">Vista global del sistema Olympic Academy</p>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <component
            :is="stat.href ? 'router-link' : 'div'"
            v-for="stat in statsCards"
            :key="stat.label"
            :to="stat.href || undefined"
            class="stat-card"
          >
            <div class="stat-card-inner">
              <div>
                <p class="stat-card-label">{{ stat.label }}</p>
                <p class="stat-card-value">{{ stat.value }}</p>
              </div>
              <div class="stat-icon-wrap" :style="{ background: `color-mix(in srgb, ${stat.accent} 12%, transparent)`, color: stat.accent }">
                <q-icon :name="stat.icon" size="20px" />
              </div>
            </div>
            <div v-if="stat.href" class="stat-card-link">
              Ver detalle <q-icon name="arrow_forward" size="12px" />
            </div>
          </component>
        </div>

        <!-- Role Distribution + Recent Users -->
        <div class="admin-two-col">
          <!-- Role Distribution -->
          <div class="admin-card">
            <div class="admin-card-header">
              <h2 class="admin-card-title">Distribucion por rol</h2>
              <router-link to="/admin/users" class="admin-card-link">Ver todos</router-link>
            </div>
            <div class="role-list">
              <div v-for="entry in roleEntries" :key="entry.role" class="role-item">
                <div class="role-item-header">
                  <span class="role-item-name">{{ roleLabels[entry.role] || entry.role }}</span>
                  <span class="role-item-count">{{ entry.count }}</span>
                </div>
                <div class="role-bar-track">
                  <div
                    class="role-bar-fill"
                    :style="{ width: totalUsers > 0 ? ((entry.count / totalUsers) * 100) + '%' : '0%' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity / Audit Log -->
          <div class="admin-card">
            <h2 class="admin-card-title">Cambios de rol recientes</h2>
            <template v-if="auditLog.length > 0">
              <div class="audit-list">
                <div v-for="change in auditLog" :key="change.id" class="audit-item">
                  <div class="audit-item-left">
                    <span class="audit-item-name">{{ change.userName }}</span>
                    <span class="audit-arrow">-></span>
                    <q-badge
                      :label="roleLabels[change.toRole] || change.toRole"
                      :style="{
                        background: 'color-mix(in srgb, var(--oa-gold-full) 10%, transparent)',
                        color: 'var(--oa-gold-full)',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '8px',
                      }"
                    />
                  </div>
                  <span class="audit-item-meta">
                    <template v-if="change.promoterName">por {{ change.promoterName }}</template>
                    {{ formatDate(change.createdAt) }}
                  </span>
                </div>
              </div>
            </template>
            <p v-else class="no-data-text">Sin cambios recientes</p>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <router-link
            v-for="action in quickActions"
            :key="action.to"
            :to="action.to"
            class="quick-action-card"
          >
            <q-icon :name="action.icon" size="20px" style="color: var(--oa-gold-full);" />
            <div>
              <p class="quick-action-title">{{ action.title }}</p>
              <p class="quick-action-desc">{{ action.desc }}</p>
            </div>
          </router-link>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface Stats {
  users: number;
  courses: number;
  enrollments: number;
  activeEnrollments: number;
  sedes: number;
  certificates: number;
  submissions: number;
  achievementsUnlocked: number;
}

interface RoleEntry {
  role: string;
  count: number;
}

interface AuditEntry {
  id: string;
  userName: string;
  toRole: string;
  promoterName: string | null;
  createdAt: string;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const stats = ref<Stats | null>(null);
const roleDistribution = ref<RoleEntry[]>([]);
const auditLog = ref<AuditEntry[]>([]);

// ---------- Constants ----------
const roleLabels: Record<string, string> = {
  aspirante: 'Aspirante', cc: 'Coach Certified', sc: 'Senior Coach',
  c1: 'Coach Instructor', c2: 'Lead Trainer', ot_candidato: 'OT Candidato',
  ot_1: 'OT-1', ot_2: 'OT-2', ot_3: 'OT-3', owner: 'Owner',
  oa: 'Olympic Auditor', gj: 'Games Judge', coordinator: 'Coordinador',
  admin: 'Admin', super_admin: 'Super Admin',
};

const quickActions = [
  { to: '/admin/users', icon: 'people', title: 'Gestionar Usuarios', desc: 'Roles, busqueda, activar/desactivar' },
  { to: '/admin/courses', icon: 'school', title: 'Gestionar Cursos', desc: 'Publicar, despublicar, ver estado' },
  { to: '/admin/certificates', icon: 'verified', title: 'Certificados', desc: 'Emitidos, vigentes, expirados' },
];

// ---------- Computed ----------
const totalUsers = computed(() => roleDistribution.value.reduce((sum, e) => sum + e.count, 0));

const roleEntries = computed(() =>
  [...roleDistribution.value].sort((a, b) => b.count - a.count),
);

const statsCards = computed(() => {
  if (!stats.value) return [];
  const s = stats.value;
  return [
    { label: 'Usuarios', value: s.users, icon: 'people', href: '/admin/users', accent: 'var(--oa-gold-full)' },
    { label: 'Cursos', value: s.courses, icon: 'school', href: '/admin/courses', accent: 'var(--oa-tier-cc)' },
    { label: 'Inscripciones activas', value: s.activeEnrollments, icon: 'trending_up', href: null, accent: 'var(--oa-gold-full)' },
    { label: 'Sedes', value: s.sedes, icon: 'location_on', href: '/sede', accent: 'var(--oa-tier-c1)' },
    { label: 'Certificados', value: s.certificates, icon: 'emoji_events', href: '/admin/certificates', accent: 'var(--oa-gold-full)' },
    { label: 'Entregas', value: s.submissions, icon: 'assignment_turned_in', href: '/evaluations', accent: '#f87171' },
    { label: 'Logros desbloqueados', value: s.achievementsUnlocked, icon: 'bolt', href: '/achievements', accent: 'var(--oa-tier-ot)' },
    { label: 'Inscripciones total', value: s.enrollments, icon: 'verified_user', href: null, accent: 'var(--oa-text-muted)' },
  ];
});

// ---------- Helpers ----------
function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

// ---------- Data Fetching ----------
async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const [statsRes, rolesRes, auditRes] = await Promise.all([
      api.get('/admin'),
      api.get('/admin/role-distribution'),
      api.get('/admin/audit-log', { params: { limit: 5 } }).catch(() => ({ data: { items: [] } })),
    ]);
    stats.value = statsRes.data;
    roleDistribution.value = rolesRes.data;
    auditLog.value = (auditRes.data.items || auditRes.data || []);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar panel de admin';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.admin-page {
  padding: 24px;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
}

.admin-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 12px;
}

.empty-text {
  color: var(--oa-text-muted);
  font-size: 14px;
}

.no-data-text {
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

/* Header */
.admin-header {
  margin-bottom: 24px;
}

.admin-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.admin-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 20px;
  text-decoration: none;
  transition: box-shadow 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.stat-card-inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.stat-card-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--oa-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.stat-card-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 4px 0 0;
}

.stat-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--oa-gold-full);
  font-weight: 500;
  margin-top: 12px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.stat-card:hover .stat-card-link {
  opacity: 1;
}

/* Two column */
.admin-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .admin-two-col {
    grid-template-columns: 1fr;
  }
}

/* Card */
.admin-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
}

.admin-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.admin-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
}

.admin-card-header .admin-card-title {
  margin: 0;
}

.admin-card-link {
  font-size: 0.75rem;
  color: var(--oa-gold-full);
  font-weight: 500;
  text-decoration: none;
}

.admin-card-link:hover {
  text-decoration: underline;
}

/* Role distribution */
.role-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.role-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.role-item-name {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--oa-text-primary);
}

.role-item-count {
  font-size: 0.8125rem;
  color: var(--oa-text-muted);
}

.role-bar-track {
  width: 100%;
  height: 6px;
  background: var(--oa-bg-3);
  border-radius: 3px;
}

.role-bar-fill {
  height: 6px;
  background: linear-gradient(90deg, var(--oa-gold-mid), var(--oa-gold-full));
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Audit log */
.audit-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audit-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
}

.audit-item-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audit-item-name {
  font-weight: 500;
  color: var(--oa-text-primary);
}

.audit-arrow {
  color: var(--oa-text-muted);
  font-size: 0.75rem;
}

.audit-item-meta {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
}

/* Quick Actions */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 768px) {
  .quick-actions {
    grid-template-columns: 1fr;
  }
}

.quick-action-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  text-decoration: none;
  transition: border-color 0.2s ease;
}

.quick-action-card:hover {
  border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
}

.quick-action-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.quick-action-desc {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}
</style>
